// Discrete, non blocking notifications of the advisor workstation.
//
// A toast confirms an action that already succeeded or reports a recoverable
// failure. It never blocks the interface, never traps the focus and never
// carries the only copy of an important message: destructive confirmations go
// through a modal, and blocking failures go through the state cards of the
// screens. The queue is a module singleton so any store or component can push
// into it without prop drilling.
import { readonly, ref } from 'vue';
import type { DeepReadonly, Ref } from 'vue';

/** Semantic tone, mapped to the --hz-*-bg / --hz-*-fg token pairs. */
export type TonToast = 'succes' | 'erreur' | 'information';

export interface Toast {
  readonly id: number;
  readonly ton: TonToast;
  /** French sentence displayed to the advisor. */
  readonly message: string;
  /** Optional second line, typically the request identifier of a failure. */
  readonly detail?: string;
}

/** Display duration imposed by the design pack. */
export const DUREE_TOAST_MS = 4000;

/** Beyond this many simultaneous toasts the oldest one is dropped. */
const PILE_MAX = 3;

const file = ref<Toast[]>([]);
const minuteries = new Map<number, ReturnType<typeof setTimeout>>();
let compteur = 0;

function annulerMinuterie(id: number): void {
  const minuterie = minuteries.get(id);
  if (minuterie !== undefined) {
    clearTimeout(minuterie);
    minuteries.delete(id);
  }
}

/** Remove a toast immediately, for example when the advisor dismisses it. */
export function retirerToast(id: number): void {
  annulerMinuterie(id);
  file.value = file.value.filter((toast) => toast.id !== id);
}

/** Drop every pending toast, used when the session ends. */
export function viderToasts(): void {
  for (const toast of file.value) {
    annulerMinuterie(toast.id);
  }
  file.value = [];
}

/** Queue a toast and schedule its automatic dismissal. Returns its identifier. */
export function ajouterToast(ton: TonToast, message: string, detail?: string): number {
  compteur += 1;
  const id = compteur;
  const toast: Toast = detail === undefined ? { id, ton, message } : { id, ton, message, detail };
  const suite = [...file.value, toast];
  while (suite.length > PILE_MAX) {
    const retire = suite.shift();
    if (retire !== undefined) {
      annulerMinuterie(retire.id);
    }
  }
  file.value = suite;
  minuteries.set(
    id,
    setTimeout(() => {
      retirerToast(id);
    }, DUREE_TOAST_MS),
  );
  return id;
}

export interface ApiToasts {
  /** Reactive queue, oldest first. */
  readonly toasts: DeepReadonly<Ref<Toast[]>>;
  readonly succes: (message: string, detail?: string) => number;
  readonly erreur: (message: string, detail?: string) => number;
  readonly information: (message: string, detail?: string) => number;
  readonly retirer: (id: number) => void;
  readonly vider: () => void;
  readonly dureeMs: number;
}

/** Access the shared toast queue from a component or a store. */
export function useToasts(): ApiToasts {
  return {
    toasts: readonly(file),
    succes: (message, detail) => ajouterToast('succes', message, detail),
    erreur: (message, detail) => ajouterToast('erreur', message, detail),
    information: (message, detail) => ajouterToast('information', message, detail),
    retirer: retirerToast,
    vider: viderToasts,
    dureeMs: DUREE_TOAST_MS,
  };
}
